import React, { useState } from 'react'
import {
  Shield,
  Lock,
  Eye,
  Trash2,
  Download,
  FileEdit,
  Ban,
  AlertTriangle,
  CheckCircle,
  Mail,
  User,
  Building2,
  XCircle,
  Info,
  Cookie,
  FileText,
  HelpCircle,
  Loader
} from 'lucide-react'
import { useTenant } from '../contexts/TenantContext'

const PrivacyPortalSection: React.FC = () => {
  const { config } = useTenant()
  const [activeTab, setActiveTab] = useState<'overview' | 'requests' | 'policy' | 'cookies' | 'dpa' | 'faq'>('overview')
  const [privacyRequestStep, setPrivacyRequestStep] = useState<'form' | 'verification' | 'success'>('form')
  const [requestType, setRequestType] = useState<string>('access')
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    description: ''
  })
  const [verificationCode, setVerificationCode] = useState('')
  const [requestId, setRequestId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const requestTypes = [
    {
      id: 'access',
      icon: Eye,
      title: 'Access My Data',
      description: 'Request a copy of your personal data',
      law: 'GDPR Art. 15 / CCPA §1798.100'
    },
    {
      id: 'delete',
      icon: Trash2,
      title: 'Delete My Data',
      description: 'Request deletion of your personal data',
      law: 'GDPR Art. 17 / CCPA §1798.105'
    },
    {
      id: 'portability',
      icon: Download,
      title: 'Data Portability',
      description: 'Receive your data in machine-readable format',
      law: 'GDPR Art. 20'
    },
    {
      id: 'rectification',
      icon: FileEdit,
      title: 'Correct My Data',
      description: 'Request correction of inaccurate data',
      law: 'GDPR Art. 16 / CCPA §1798.106'
    }
  ]

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      // Safety improvement: Validate config exists before accessing properties
      if (!config) {
        throw new Error('Configuration not available')
      }

      const response = await fetch('/api/privacy/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          requestType,
          verificationMethod: 'email',
          tenantId: config.tenantId,
          version: config.version
        })
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const data = await response.json()
          errorMessage = data.error || errorMessage
        } catch (parseErr) {
          console.error('[PrivacyPortalSection] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[PrivacyPortalSection] Submit request failed:', errorMessage)
        throw new Error(errorMessage)
      }

      const data = await response.json()
      setRequestId(data.requestId)
      setPrivacyRequestStep('verification')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyRequest = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      // Safety improvement: Validate config exists before accessing properties
      if (!config) {
        throw new Error('Configuration not available')
      }

      const response = await fetch('/api/privacy/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId,
          code: verificationCode,
          tenantId: config.tenantId,
          version: config.version
        })
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        try {
          const data = await response.json()
          errorMessage = data.error || errorMessage
        } catch (parseErr) {
          console.error('[PrivacyPortalSection] Failed to parse error response:', parseErr)
          // Use generic message
        }
        console.error('[PrivacyPortalSection] Verification failed:', errorMessage)
        throw new Error(errorMessage)
      }

      setPrivacyRequestStep('success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Shield className="h-10 w-10 text-primary-600 mr-3" />
            <h2 className="text-3xl font-bold text-gray-900">Privacy Portal</h2>
          </div>
          <p className="text-lg text-gray-600">
            Exercise your privacy rights under GDPR, CCPA, and other data protection laws
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'requests'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Submit Request
            </button>
            <button
              onClick={() => setActiveTab('policy')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'policy'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Privacy Policy
            </button>
            <button
              onClick={() => setActiveTab('cookies')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cookies'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Cookie Settings
            </button>
            <button
              onClick={() => setActiveTab('faq')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'faq'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              FAQs
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-2">Your Privacy Rights</p>
                  <p className="text-blue-800">
                    Under GDPR, CCPA, and other privacy laws, you have the right to access, correct, delete,
                    and control how your personal data is used. We take these rights seriously and will respond
                    to your request within 30 days (or 45 days under CCPA).
                  </p>
                </div>
              </div>
            </div>

            {/* Privacy Rights Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {requestTypes.map((type) => {
                const IconComponent = type.icon
                return (
                  <div key={type.id} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="flex items-start">
                      <IconComponent className="h-6 w-6 text-primary-600 mt-1 mr-4 flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{type.title}</h3>
                        <p className="text-gray-600 mb-3">{type.description}</p>
                        <p className="text-xs text-gray-500">{type.law}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* CTA */}
            <div className="text-center bg-white rounded-lg p-8 shadow-sm border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Ready to Exercise Your Rights?</h3>
              <p className="text-gray-600 mb-6">
                Submit a privacy request and we'll respond within the legally required timeframe.
              </p>
              <button
                onClick={() => setActiveTab('requests')}
                className="btn-primary inline-flex items-center"
              >
                Submit Privacy Request
                <CheckCircle className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div className="max-w-3xl mx-auto">
            {privacyRequestStep === 'form' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Submit Privacy Request</h3>

                <form onSubmit={handleSubmitRequest} className="space-y-6">
                  {/* Request Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Request Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {requestTypes.map((type) => {
                        const IconComponent = type.icon
                        return (
                          <button
                            key={type.id}
                            type="button"
                            onClick={() => setRequestType(type.id)}
                            className={`text-left p-4 border-2 rounded-lg transition-all ${
                              requestType === type.id
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex items-start space-x-3">
                              <IconComponent className={`h-5 w-5 mt-0.5 ${
                                requestType === type.id ? 'text-primary-600' : 'text-gray-400'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900 text-sm">{type.title}</h4>
                                <p className="text-xs text-gray-600 mt-1">{type.description}</p>
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Company/Organization (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Company Name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Details (Optional)
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Provide any additional context..."
                    />
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-4">
                    <p className="text-xs text-gray-500">
                      By submitting, you confirm you are the data subject or authorized to act on their behalf.
                    </p>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="h-4 w-4 animate-spin mr-2" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Request'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {privacyRequestStep === 'verification' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Verify Your Identity</h3>
                  <p className="text-gray-600">
                    We've sent a verification code to <strong>{formData.email}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerifyRequest} className="max-w-md mx-auto">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      required
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                    />
                  </div>

                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || verificationCode.length !== 6}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Submit'
                    )}
                  </button>
                </form>
              </div>
            )}

            {privacyRequestStep === 'success' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted Successfully</h3>
                  <p className="text-gray-600 mb-6">
                    Your privacy request has been received and is being processed.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <p className="text-sm font-medium text-gray-700 mb-2">Request ID</p>
                    <p className="text-lg font-mono text-gray-900">{requestId}</p>
                  </div>

                  <button
                    onClick={() => {
                      setPrivacyRequestStep('form')
                      setFormData({ email: '', name: '', company: '', description: '' })
                      setVerificationCode('')
                      setRequestId('')
                    }}
                    className="btn-secondary"
                  >
                    Submit Another Request
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Privacy Policy Tab */}
        {activeTab === 'policy' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">Privacy Policy</h3>
            <div className="prose max-w-none">
              <p className="text-gray-600 mb-4">
                This is a placeholder for your comprehensive privacy policy. A full privacy policy should include:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                <li>What personal data we collect</li>
                <li>How we use your data</li>
                <li>Legal basis for processing (GDPR)</li>
                <li>Data retention periods</li>
                <li>Your rights as a data subject</li>
                <li>International data transfers</li>
                <li>Cookie usage</li>
                <li>Contact information for privacy inquiries</li>
              </ul>
              <p className="text-sm text-gray-500 italic">
                Note: Consult with legal counsel to create a comprehensive privacy policy for your organization.
              </p>
            </div>
          </div>
        )}

        {/* Cookies Tab */}
        {activeTab === 'cookies' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <Cookie className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-2xl font-semibold text-gray-900">Cookie Settings</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Manage your cookie preferences. This is a placeholder for cookie consent management.
            </p>
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Essential Cookies</h4>
                    <p className="text-sm text-gray-600">Required for the website to function</p>
                  </div>
                  <div className="text-sm text-gray-500">Always Active</div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Analytics Cookies</h4>
                    <p className="text-sm text-gray-600">Help us improve our website</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="flex items-center mb-6">
              <HelpCircle className="h-6 w-6 text-primary-600 mr-3" />
              <h3 className="text-2xl font-semibold text-gray-900">Privacy FAQs</h3>
            </div>
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">How long does it take to process my request?</h4>
                <p className="text-gray-600">
                  We aim to respond to all privacy requests within 30 days under GDPR or 45 days under CCPA.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What information do I need to provide?</h4>
                <p className="text-gray-600">
                  We require your name and email address to verify your identity and process your request.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Can I request data deletion?</h4>
                <p className="text-gray-600">
                  Yes, you have the right to request deletion of your personal data, subject to certain legal exceptions.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PrivacyPortalSection
